import { isValid } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/react/utils';
import { cloneDeep } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import {
  createITransactionDataModel,
  ITransactionDataModel,
  RestructureShareData,
} from '../data-models/transaction.data-model';
import { MetricsTransactionDataModel } from '../schemas/MetricsTransaction.schema';
import { transactionTypesByIdMapAtom } from '../services/state/AppConfigStateJ';
import { companyMetricsAtomJ } from '../services/state/CompanyMetricsStateJ';
import { TRANSACTION_SUB_TYPES } from '../types';
import { dateToPlainDateString } from '../util/date-utilts';
import { isNumber } from '../util/numericUtils';
import { isObject } from '../util/object-util';
import { TransactionTypeService } from '../util/transaction-type-service';

export enum WarrantsType {
  numberOfWarrants = 'Number of Warrants',
  totalValueOfWarrants = 'Total Value of Warrants',
}

export enum SellBy {
  shares = 'Shares',
  returnOfCapitalAmount = 'Return of Capital Amount',
}

export interface ITransactionViewModelInternal {
  exchangedSharesByRestructureId?: Record<string, number> | null;
  // Field-keyed like its sibling. `undefined` means the user cleared the row, so
  // isNumber() rejects it and the computed quantity applies again.
  userExchangedSharesByRestructureId?: Record<string, number | undefined>;
  ppsShareClass?: Record<string, number> | null;
  selectedSecurityForSharesSold?: string[];
  receivedSharesByRestructureId?: Record<string, number>;
  receivedSecurityByRestructureId?: Record<string, string>;
  receivedPpsByRestructureId?: Record<string, number>;
  showPpsShareClass?: boolean;
  transactionCategory?: string;
  useSplitByRestructureId: boolean;
  warrantsType?: WarrantsType;
  sellBy?: SellBy;
}
export interface ITransactionViewModel extends ITransactionDataModel {
  _viewModel: ITransactionViewModelInternal;
}

export function createTransactionViewModel(
  overrides: Partial<ITransactionViewModel> = {}
): ITransactionViewModel {
  return {
    ...createITransactionDataModel({
      ...overrides,
    }),
    _viewModel: {
      exchangedSharesByRestructureId: {},
      userExchangedSharesByRestructureId: {},
      ppsShareClass: {},
      receivedSharesByRestructureId: {},
      receivedSecurityByRestructureId: {},
      receivedPpsByRestructureId: {},
      selectedSecurityForSharesSold: [],
      showPpsShareClass: false,
      useSplitByRestructureId: false,
      warrantsType: undefined,
      sellBy: SellBy.shares,
      ...overrides._viewModel,
    },
  };
}

export function useCreateTransactionViewModel(companyId: number) {
  const metrics = useAtomValue(companyMetricsAtomJ({ companyId }));
  // Derived from the suspended value rather than metricsFundDataByFundIdAtomJ:
  // that atom is `unwrap`ped, so its first read returns an empty map even for an
  // already-settled promise, and useForm latches the resulting fund on render one.
  const companyFundIds = useMemo(() => {
    return (metrics?.metrics?.at(0)?.fundData ?? []).map(({ fundId }) => fundId);
  }, [metrics]);

  return useAtomCallback(
    useCallback(
      (get, _set, subtypeId: number): Partial<ITransactionViewModel> => {
        const transactionTypeMap = get(transactionTypesByIdMapAtom);
        const transactionType = transactionTypeMap.get(subtypeId ?? -1)?.name;
        const transactions = metrics?.metrics[0]?.transactions ?? [];
        const transactionViewModel = createTransactionViewModel({
          fundId: companyFundIds?.at(0) ?? null,
          id: undefined,
          companyId: companyId ?? -1,
          transactionTypeId: subtypeId,
          transactionDate: dateToPlainDateString(new Date()),
        });

        // When we create a new transaction, split by restructureId is always false, therefore we only need
        // to populate exchangedSharesByRestructureId for share exchange transactions
        if (transactionType === TRANSACTION_SUB_TYPES.SHARE_EXCHANGE) {
          transactionViewModel._viewModel.useSplitByRestructureId = false;
          transactionViewModel._viewModel.exchangedSharesByRestructureId = getShareExchangeDetails(
            transactions,
            companyFundIds?.at(0) ?? null,
            new Date(),
            {}
          );
        }

        return transactionViewModel;
      },
      [companyFundIds, companyId, metrics?.metrics]
    )
  );
}

export function useFromTransactionDataModel(companyId: number) {
  const metrics = useAtomValue(companyMetricsAtomJ({ companyId }));

  return useAtomCallback(
    useCallback(
      (get, _set, transaction: Partial<ITransactionDataModel>): Partial<ITransactionViewModel> => {
        const transactionTypeMap = get(transactionTypesByIdMapAtom);
        const subtypeId = transaction.transactionTypeId;
        const transactionType = transactionTypeMap.get(subtypeId ?? -1)?.name;
        const transactions = metrics?.metrics[0]?.transactions ?? [];
        const transactionViewModel = createTransactionViewModel(cloneDeep(transaction));

        transactionViewModel._viewModel = {
          showPpsShareClass:
            isObject(transaction.ppsShareClass && Object.keys(transaction.ppsShareClass).length > 0) &&
            transaction.valuationImpact === true,
          ppsShareClass: transaction.ppsShareClass
            ? getPpsPerShareClassViewModel(transaction.ppsShareClass)
            : {},
          transactionCategory: transactionTypeMap.get(subtypeId ?? -1)?.catergory ?? undefined,
          useSplitByRestructureId: false,
        };

        if (transactionType === TRANSACTION_SUB_TYPES.WARRANT_ISSUANCE) {
          if (transaction.aggregatedValue) {
            transactionViewModel._viewModel.warrantsType = WarrantsType.totalValueOfWarrants;
          } else {
            transactionViewModel._viewModel.warrantsType = WarrantsType.numberOfWarrants;
          }
        } else if (transactionType === TRANSACTION_SUB_TYPES.SELL) {
          transactionViewModel._viewModel.sellBy =
            transaction.returnOfCapitalAmount != null ? SellBy.returnOfCapitalAmount : SellBy.shares;
          transactionViewModel._viewModel.useSplitByRestructureId =
            transaction.exchangedSharesByRestructureId != null;
          transactionViewModel._viewModel.selectedSecurityForSharesSold = Object.keys(
            transaction.exchangedSharesByRestructureId ?? {}
          );
          transactionViewModel._viewModel.exchangedSharesByRestructureId =
            transaction.exchangedSharesByRestructureId
              ? getPpsPerShareClassViewModel(transaction.exchangedSharesByRestructureId)
              : {};
        } else if (transactionType === TRANSACTION_SUB_TYPES.SHARE_EXCHANGE) {
          // For share exchange, we always populate exchangedSharesByRestructureId
          transactionViewModel._viewModel.exchangedSharesByRestructureId = getShareExchangeDetails(
            transactions,
            transaction.fundId,
            transaction.transactionDate ? new Date(transaction.transactionDate) : new Date(),
            transaction.exchangedSharesByRestructureId ?? {}
          );
          // Saved quantities are user input too, so editing is as safe as creating
          transactionViewModel._viewModel.userExchangedSharesByRestructureId = getPpsPerShareClassViewModel(
            transaction.exchangedSharesByRestructureId ?? {}
          );

          transactionViewModel._viewModel.useSplitByRestructureId =
            transaction.receivedSharesByRestructureId != null;

          // If the transaction is split by restructureId, we need to get the received meta data too
          if (transactionViewModel._viewModel.useSplitByRestructureId) {
            const receivedData = fromReceivedSharesByRestructureIdDataModel(
              transaction.receivedSharesByRestructureId
            );
            transactionViewModel._viewModel.receivedSharesByRestructureId =
              receivedData.receivedSharesByRestructureId;
            transactionViewModel._viewModel.receivedSecurityByRestructureId =
              receivedData.receivedSecurityByRestructureId;
            transactionViewModel._viewModel.receivedPpsByRestructureId =
              receivedData.receivedPpsByRestructureId;
          }
        }

        return transactionViewModel;
      },
      [metrics?.metrics]
    )
  );
}

export function toTransactionDataModel(
  transactionViewModel: Partial<ITransactionViewModel>
): Partial<ITransactionDataModel> {
  const { _viewModel, ...dataModel } = transactionViewModel;
  const transaction = cloneDeep(dataModel);
  const transTypeSvc = TransactionTypeService.get();
  const transactionType = transTypeSvc.getTransactionType(dataModel.transactionTypeId ?? -1);

  if (transactionType === TRANSACTION_SUB_TYPES.SELL) {
    // Nulls, not undefined: the api merge skips undefined, so the field left behind by a mode switch would survive an edit.
    if (_viewModel?.sellBy === SellBy.returnOfCapitalAmount) {
      transaction.noOfShares = null;
      transaction.exchangedSharesByRestructureId = null;
    } else if (_viewModel?.useSplitByRestructureId === true && _viewModel?.exchangedSharesByRestructureId) {
      transaction.returnOfCapitalAmount = null;
      transaction.exchangedSharesByRestructureId = getPpsPerShareClassDataModel(
        _viewModel.exchangedSharesByRestructureId
      );
    } else {
      transaction.returnOfCapitalAmount = null;
      // BE use the existence of this field to determine if the transaction is split by restructureId or not
      delete transaction.exchangedSharesByRestructureId;
    }
  } else if (transactionType === TRANSACTION_SUB_TYPES.MISC_OWNERSHIP_CHANGE) {
    transaction.fundId = null;
  } else if (transactionType === TRANSACTION_SUB_TYPES.INTERNAL_VALUATION) {
    if (transactionViewModel.valuationImpact) {
      transaction.valuationAmount = null;
      transaction.fundId = null;
      if (_viewModel?.showPpsShareClass && _viewModel.ppsShareClass) {
        transaction.ppsShareClass = getPpsPerShareClassDataModel(_viewModel.ppsShareClass);
        transaction.pricePerShare = null;
      } else {
        transaction.ppsShareClass = {};
      }
    } else {
      transaction.pricePerShare = null;
      transaction.ppsShareClass = {};
    }
  } else if (transactionType === TRANSACTION_SUB_TYPES.SHARE_EXCHANGE) {
    if (_viewModel?.exchangedSharesByRestructureId) {
      transaction.exchangedSharesByRestructureId = getPpsPerShareClassDataModel(
        _viewModel.exchangedSharesByRestructureId
      );
    }
    if (
      _viewModel?.useSplitByRestructureId &&
      _viewModel?.receivedSharesByRestructureId &&
      _viewModel?.receivedSecurityByRestructureId &&
      _viewModel?.receivedPpsByRestructureId
    ) {
      transaction.receivedSharesByRestructureId = toReceivedSharesByRestructureIdDataModel(
        _viewModel.receivedSharesByRestructureId,
        _viewModel.receivedSecurityByRestructureId,
        _viewModel.receivedPpsByRestructureId
      );
    } else if (!_viewModel?.useSplitByRestructureId) {
      // BE use the existence of this field to determine if the transaction is split by restructureId or not
      delete transaction.receivedSharesByRestructureId;
    }
  }

  if (_viewModel?.warrantsType === WarrantsType.numberOfWarrants) {
    transaction.aggregatedValue = undefined;
  } else if (_viewModel?.warrantsType === WarrantsType.totalValueOfWarrants) {
    transaction.warrantsIssued = undefined;
    transaction.warrantAcquiredPrice = undefined;
  }

  return transaction;
}

export function getPpsPerShareClassViewModel(ppsShareClass: Record<string, number>) {
  const res: Record<string, number> = {};
  const restructuringIds = Object.keys(ppsShareClass ?? {});

  restructuringIds.forEach((restructuringId) => {
    res[restructureKeyToFieldKey(restructuringId)] = ppsShareClass[restructuringId];
  });

  return res;
}

export function getPpsPerShareClassDataModel<T>(ppsShareClass: Record<string, T>) {
  const res: Record<string, T> = {};
  const restructuringIds = Object.keys(ppsShareClass ?? {});

  restructuringIds.forEach((restructuringId) => {
    res[fieldKeyToRestructureKey(restructuringId)] = ppsShareClass[restructuringId];
  });

  return res;
}

export function toReceivedSharesByRestructureIdDataModel(
  receivedSharesByRestructureId: Record<string, number>,
  receivedSecurityByRestructureId: Record<string, string>,
  receivedPpsByRestructureId: Record<string, number>
): ITransactionDataModel['receivedSharesByRestructureId'] {
  const res: Record<string, RestructureShareData> = {};
  const restructuringIds = Object.keys(receivedSharesByRestructureId ?? {});

  restructuringIds.forEach((restructuringId) => {
    res[fieldKeyToRestructureKey(restructuringId)] = {
      noOfShares: receivedSharesByRestructureId[restructuringId],
      pricePerShare: receivedPpsByRestructureId[restructuringId],
      positionName: receivedSecurityByRestructureId[restructuringId],
    };
  });

  return res;
}

export function fromReceivedSharesByRestructureIdDataModel(
  data: ITransactionDataModel['receivedSharesByRestructureId']
): Pick<
  ITransactionViewModelInternal,
  'receivedSharesByRestructureId' | 'receivedSecurityByRestructureId' | 'receivedPpsByRestructureId'
> {
  const receivedSharesByRestructureId: Record<string, number> = {};
  const receivedSecurityByRestructureId: Record<string, string> = {};
  const receivedPpsByRestructureId: Record<string, number> = {};

  if (!data) {
    return { receivedSharesByRestructureId, receivedSecurityByRestructureId, receivedPpsByRestructureId };
  }

  const restructuringIds = Object.keys(data ?? {});

  restructuringIds.forEach((restructuringId) => {
    receivedSharesByRestructureId[restructureKeyToFieldKey(restructuringId)] =
      data[restructuringId].noOfShares;
    receivedSecurityByRestructureId[restructureKeyToFieldKey(restructuringId)] =
      data[restructuringId].positionName;
    receivedPpsByRestructureId[restructureKeyToFieldKey(restructuringId)] =
      data[restructuringId].pricePerShare;
  });

  return { receivedSharesByRestructureId, receivedSecurityByRestructureId, receivedPpsByRestructureId };
}

export function restructureKeyToFieldKey(restructureId: string) {
  return `r${restructureId.replaceAll('.', '_')}`;
}

export function fieldKeyToRestructureKey(fieldKey: string) {
  return fieldKey.replaceAll('_', '.').substring(1);
}

type IRestructureMeta = Pick<
  MetricsTransactionDataModel,
  'transactionDate' | 'fundId' | 'position' | 'quantity'
> & {
  restructureId: string;
  amount: number; // This will be calculated from investmentAmount
};

export function getRestructureIdsMeta(
  transactions: MetricsTransactionDataModel[],
  transactionFundId: number | null | undefined,
  newTransactionDate: Date
): Map<string, IRestructureMeta> {
  const res = new Map<string, IRestructureMeta>();
  const accumulatedMeta = new Map<string, { quantity: number; amount: number }>();

  if (transactions.length === 0 || !isValid(newTransactionDate)) {
    return res;
  }

  transactions.forEach((transaction) => {
    const { fundId, position, quantity, restructureId, transactionDate: transactionDateISO } = transaction;
    const transactionDate = new Date(transactionDateISO);
    const isBeforeNewTransactionDate = transactionDate.getTime() < newTransactionDate.getTime();

    // an unset fund means "every fund", not "no fund matches"
    if (transactionFundId != null && fundId !== transactionFundId) {
      return;
    }

    if (restructureId && isBeforeNewTransactionDate) {
      const currentLatestTransaction = res.get(restructureId);
      const isLatestTransaction =
        currentLatestTransaction === undefined ||
        (transactionDate.getTime() >= new Date(currentLatestTransaction.transactionDate).getTime() &&
          quantity > 0);

      if (isLatestTransaction) {
        res.set(restructureId, {
          amount: 0,
          fundId: fundId ?? currentLatestTransaction?.fundId,
          position: position ?? currentLatestTransaction?.position,
          restructureId,
          transactionDate: transactionDateISO,
          quantity: 0, // Placeholder value to satisfy TypeScript type requirements; will be replaced with the accumulated quantity during processing.
        });
      }

      const currentAcc = accumulatedMeta.get(restructureId) ?? { quantity: 0, amount: 0 };
      currentAcc.amount += transaction.escrowAmount ?? 0;
      currentAcc.quantity += quantity;
      accumulatedMeta.set(restructureId, currentAcc);
    }
  });

  for (const meta of res.values()) {
    const accData = accumulatedMeta.get(meta.restructureId)!;
    if (accData.quantity <= 0) {
      res.delete(meta.restructureId);
    } else {
      res.set(meta.restructureId, { ...meta, amount: accData.amount, quantity: accData.quantity });
    }
  }

  return res;
}

function getShareExchangeDetails(
  transactions: MetricsTransactionDataModel[],
  fundId: number | null = null,
  date: Date,
  // undefined entries are a cleared row: isNumber rejects them and the computed quantity wins
  overrides: Record<string, number | undefined>
) {
  const restructureMeta = getRestructureIdsMeta(transactions, fundId, date);
  const exchangedSharesByRestructureId: Record<string, number> = {};

  Array.from(restructureMeta.values()).forEach((transaction) => {
    const { restructureId, quantity } = transaction;
    let value = 0;
    if (isNumber(quantity)) {
      value = quantity;
    }
    if (isNumber(overrides[restructureId])) {
      value = overrides[restructureId];
    }

    exchangedSharesByRestructureId[restructureKeyToFieldKey(restructureId)] = value;
  });

  return exchangedSharesByRestructureId;
}

export function useGetShareExchangeDetails(companyId: number) {
  const metrics = useAtomValue(companyMetricsAtomJ({ companyId }));

  return useAtomCallback(
    useCallback(
      (
        _get,
        _set,
        params: {
          fundId: number | null;
          date: Date;
          overrides: Record<string, number | undefined>;
        }
      ) => {
        const transactions = metrics?.metrics[0]?.transactions ?? [];
        const { fundId, date, overrides } = params;
        return getShareExchangeDetails(transactions, fundId, date, overrides);
      },
      [metrics?.metrics]
    )
  );
}
